/**
 * User-facing hints for GitLab OAuth failures (Incognito popup, cold catalog, etc.).
 * Used by the sign-in page and the OAuth "Login Required" dialog.
 */

/** GitLab popup closed before postMessage completed (often a false negative in Incognito). */
const POPUP_CLOSED_USER_MESSAGE =
  'The GitLab window closed before this step finished. That often happens in Incognito even when GitLab actually succeeded. ' +
  'Refresh this page first—Iron Phoenix may already be signed in. If you still see this dialog, click Log in again.';

/** Map backend/OAuth error messages to hints for Incognito + cold catalog. */
export function formatAuthFailureUserMessage(
  base: string,
  errorName?: string,
): string {
  const baseLower = base.toLowerCase();

  const popupClosed =
    baseLower.includes('popup was closed') ||
    baseLower.includes('popup closed') ||
    errorName === 'PopupClosedError' ||
    errorName === 'PopupRejectedError';

  const catalogAllowlist =
    baseLower.includes('backstage user catalog') ||
    baseLower.includes('user_group') ||
    baseLower.includes('not in the backstage user catalog');
  const popupOrGenericFailure =
    baseLower.includes('login failed') ||
    baseLower.includes('rejectederror') ||
    baseLower.includes('rejected by user') ||
    baseLower.includes('authentication failed') ||
    baseLower.includes('invalid grant') ||
    baseLower.includes('invalid_grant');

  if (popupClosed) {
    return POPUP_CLOSED_USER_MESSAGE;
  }

  if (catalogAllowlist) {
    return (
      `${base}\n\n` +
      'The catalog may still be ingesting your GitLab user (especially right after starting the app). ' +
      'Refresh this page, wait a few seconds, then try Sign in again.'
    );
  }
  if (popupOrGenericFailure) {
    return (
      `${base}\n\n` +
      'In Private/Incognito, GitLab sometimes closes the popup before a session is stored, or the catalog ' +
      'is not ready yet on first load. Refresh this page once, then try Sign in again.'
    );
  }
  return base;
}

export function formatSignInPageError(err: unknown): string {
  const base = err instanceof Error ? err.message : String(err);
  const name = err instanceof Error ? err.name : undefined;
  return formatAuthFailureUserMessage(base, name);
}
